import { Capacitor } from '@capacitor/core'
import { InAppBrowser } from '@capgo/capacitor-inappbrowser'
import { t } from '@lingui/macro'
import { useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import useSWR from 'swr'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout/flex/Flex'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { P } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { useQuestionnaireServices } from '../../hooks/useQuestionnaireServices'
import { AWELL_QUESTIONNAIRE_EVENTS } from '../../pages/awell-questionnaire-completed/AwellQuestionnaireCompleted.page'

import type { PluginListenerHandle } from '@capacitor/core'

type AwellHostedPageState = { status: 'error' } | { status: 'success' } | { status: 'cancelled' } | { status: 'active' }

export function AwellHostedPage({ activityId }: { activityId: number }) {
  // Refs
  // ------------------------------------------------------------------------- /
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const handledRef = useRef(false)

  // State
  // ------------------------------------------------------------------------- /
  const [state, setState] = useState<AwellHostedPageState>({
    status: 'active',
  })

  // Hooks
  // ------------------------------------------------------------------------- /
  const navigate = useNavigate()

  const { createAwellSession } = useQuestionnaireServices()

  // Imported from swr/immutable because we don't want to recreate the session for no reason
  const awellSession = useSWR('' + activityId, createAwellSession, {
    dedupingInterval: 60000, // 1 minute
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnMount: true,
    revalidateOnReconnect: false,
  })

  const isNative = Capacitor.isNativePlatform()

  // Effects
  // ------------------------------------------------------------------------- /

  // Native: open InAppBrowser when session URL is available
  useEffect(() => {
    if (!isNative) return
    const activityUrl = awellSession.data?.data?.activity_url
    if (!activityUrl) return

    let urlChangeHandle: PluginListenerHandle | undefined
    let closeHandle: PluginListenerHandle | undefined
    let messageHandle: PluginListenerHandle | undefined

    const openBrowser = async () => {
      try {
        await InAppBrowser.openWebView({
          url: activityUrl,
          headers: {
            'user-agent': `Mozilla/5.0 (iPhone; CPU iPhone OS16_5_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/16.5.2 Mobile/15E148 Safari/604.1 mijnZiekenhuis`,
          },
          title: t({
            id: 'questionnaire.awell.hosted.title',
            message: 'Vragenlijst',
          }),
          closeModal: false,
        })

        urlChangeHandle = await InAppBrowser.addListener('urlChangeEvent', (event) => {
          if (handledRef.current || !event.url.includes('awell-questionnaire-completion')) {
            return
          }

          handledRef.current = true

          try {
            const url = new URL(event.url)
            const awellStatus = url.searchParams.get('awellStatus')

            void InAppBrowser.close()

            if (awellStatus === 'success') {
              navigate(`/task/activity_${activityId}`)
            } else if (awellStatus === 'cancelled') {
              navigate(`/task/activity_${activityId}`)
            } else {
              setState({ status: 'error' })
            }
          } catch (error) {
            console.error('Failed to parse Awell redirect URL:', error)
            void InAppBrowser.close()
            setState({ status: 'error' })
          }
        })

        messageHandle = await InAppBrowser.addListener('messageFromWebview', (event) => {
          if (handledRef.current) return
          const message = event.detail?.message

          switch (message) {
            case AWELL_QUESTIONNAIRE_EVENTS.COMPLETED:
            case AWELL_QUESTIONNAIRE_EVENTS.CANCELLED: {
              handledRef.current = true
              void InAppBrowser.close()
              navigate(`/task/activity_${activityId}`)
              break
            }
            case AWELL_QUESTIONNAIRE_EVENTS.CLOSED: {
              handledRef.current = true
              void InAppBrowser.close()
              navigate(-1)
              break
            }
            case AWELL_QUESTIONNAIRE_EVENTS.ERROR: {
              handledRef.current = true
              void InAppBrowser.close()
              setState({ status: 'error' })
              break
            }
          }
        })

        closeHandle = await InAppBrowser.addListener('closeEvent', () => {
          if (handledRef.current) return
          handledRef.current = true
          navigate(-1)
        })
      } catch (error) {
        console.error('Failed to open Awell in InAppBrowser:', error)
        setState({ status: 'error' })
      }
    }

    void openBrowser()

    return () => {
      urlChangeHandle?.remove()
      closeHandle?.remove()
      messageHandle?.remove()
    }
  }, [isNative, awellSession.data?.data?.activity_url])

  // Web: listen for postMessage events from the iframe
  useEffect(() => {
    if (isNative) return

    const handler = (event: MessageEvent) => {
      const data = event.data
      switch (data) {
        case AWELL_QUESTIONNAIRE_EVENTS.COMPLETED: {
          console.log('awell questionnaire completed')
          navigate(`/task/activity_${activityId}`)
          break
        }
        case AWELL_QUESTIONNAIRE_EVENTS.CANCELLED: {
          console.log('awell questionnaire cancelled')

          setState({
            status: 'cancelled',
          })

          break
        }
        case AWELL_QUESTIONNAIRE_EVENTS.ERROR: {
          console.log('awell questionnaire error')
          setState({
            status: 'error',
          })

          break
        }
        case AWELL_QUESTIONNAIRE_EVENTS.CLOSED: {
          console.log('awell questionnaire closed')

          navigate(-1)

          break
        }
        default: {
          console.log('awell questionnaire unknown event', data)
        }
      }
    }

    window.addEventListener('message', handler)

    // clean up
    return () => {
      window.removeEventListener('message', handler)
    }
  }, [])

  useEffect(() => {
    if (awellSession.error) {
      setState({
        status: 'error',
      })
    }
  }, [awellSession.error])

  // Render
  // ------------------------------------------------------------------------- /

  switch (state.status) {
    case 'error': {
      return (
        <HostedPageStatus
          title={t({
            id: 'questionnaire.awell.hosted.error.title',
            message: 'Er liep iets mis',
          })}
          body={t({
            id: 'questionnaire.awell.hosted.error.body',
            message: 'Er is een fout opgetreden bij het ophalen van de vragenlijst. Probeer het later nog eens.',
          })}
          buttonLabel={t({
            id: 'questionnaire.awell.hosted.error.retry',
            message: 'Probeer opnieuw',
          })}
          secondaryButtonLabel={t({
            id: 'questionnaire.awell.hosted.error.cancel',
            message: 'Ga terug',
          })}
          onSecondaryButtonClick={() => navigate(-1)}
          onButtonClick={() => navigate(-1)}
        />
      )
    }
    case 'active': {
      // On native, the InAppBrowser opens as an overlay — nothing to render
      if (isNative) return null

      return (
        <StIframe
          ref={iframeRef}
          src={awellSession.data?.data?.activity_url ?? ''}
          title={t({
            id: 'questionnaire.awell.hosted.title',
            message: 'Vragenlijst',
          })}
        />
      )
    }
    case 'cancelled': {
      return <Navigate to={`/task/activity_${activityId}`} />
    }

    default: {
      return null
    }
  }
}

function HostedPageStatus({
  title,
  body,
  buttonLabel,
  secondaryButtonLabel,
  onButtonClick,
  onSecondaryButtonClick,
}: {
  title: string
  body: string
  buttonLabel: string
  secondaryButtonLabel?: string
  onButtonClick: () => void
  onSecondaryButtonClick?: () => void
}) {
  const theme = useTheme()
  return (
    <ContentPageLayout>
      <FlexColumn alignItems='center' justifyContent='center' style={{ height: '100%' }} gap={theme.UI.Spacing.Space6}>
        <H3 textAlign='center'>{title}</H3>
        <P type='BodyLargeRegular' textAlign='center'>
          {body}
        </P>
        <FlexColumn gap={theme.UI.Spacing.Space2} style={{ paddingBottom: 'var(--safe-area-inset-bottom, 0px)' }}>
          <Button variant='Primary' onClick={onButtonClick}>
            {buttonLabel}
          </Button>
          {onSecondaryButtonClick && secondaryButtonLabel ? (
            <Button variant='Secondary' onClick={onSecondaryButtonClick}>
              {secondaryButtonLabel}
            </Button>
          ) : null}
        </FlexColumn>
      </FlexColumn>
    </ContentPageLayout>
  )
}

const StIframe = styled.iframe`
  border: none;
  width: 100%;
  height: 100%;
  aspect-ratio: auto;

  padding-left: var(--safe-area-inset-left);
  padding-right: var(--safe-area-inset-right);
  border-bottom: var(--safe-area-inset-bottom) solid #fafbfc;
`
